<template>
  <Fragment>
    <div class="d-flex align-items-center">
      <div
        class="flex-grow-1 mr-3 d-flex align-items-center justify-content-end"
        style="height:40px;"
      >
        <audio
          v-if="url"
          :controls="undefined"
          style="height:40px;min-width:150px;"
        >
          Your browser does not support audio
          <source :src="url">
        </audio>
        <p
          v-if="!url && !isRecording"
          class="text-muted mb-0 text-right"
          style="line-height:1.2"
        >
          Click the "Record" button to begin audio recording.
        </p>
        <p
          v-if="isRecording"
          class="text-muted mb-0 text-right"
          style="line-height:1.2"
        >
          Recording started...
        </p>
      </div>

      <RsButton
        v-show="!isRecording"
        variant="secondary"
        @click="handleStartRecording"
      >
        <i class="fa fa-microphone mr-3" />Record
      </RsButton>

      <RsButton
        v-show="isRecording"
        variant="warning"
        @click="handleStopRecording"
      >
        <i class="fa fa-microphone mr-3 blink" />Stop
      </RsButton>

      <RsButton
        v-show="url && !isRecording"
        variant="error"
        class="ml-2"
        @click="handleDeleteRecording"
      >
        <i class="fa fa-redo mr-3" />Reset
      </RsButton>
    </div>
  </Fragment>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

declare const MediaRecorder: any;

export default defineComponent({
  name: 'RsFormAudio',

  props: {
    currentUrl: {
      type: String,
      default: '',
    },
    outFilename: {
      type: String,
      default: 'form-audio.mp3',
    },
  },

  data() {
    return {
      currentRecording: null as null | { audioUrl?: string },
      recorder: null as any,
      isRecording: false,
    };
  },

  computed: {
    url(): string {
      return this.currentRecording?.audioUrl || this.currentUrl;
    },
  },

  methods: {
    async handleStartRecording() {
      this.recorder = await this.getRecorder();
      this.recorder.start();
      this.isRecording = true;
    },

    async handleStopRecording() {
      this.currentRecording = await this.recorder.stop();
      this.isRecording = false;
    },

    handleDeleteRecording() {
      this.recorder = {};
      this.currentRecording = {};
    },

    // MediaStreamRecordingAPI Documentation - https://developer.mozilla.org/en-US/docs/Web/API/MediaStream_Recording_API
    getRecorder(): Promise<{ start: unknown, stop: () => Promise<unknown> }> {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve) => {
        const stream : MediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // Use whichever format the browser natively supports for capture. The recorded chunks will be transcoded to WAV before being emitted so
        // the uploaded file is always raw PCM — universally compatible with Twilio and PSTN phone systems regardless of browser.
        const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';

        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        const audioChunks: any[] = [];

        mediaRecorder.addEventListener('dataavailable', (event: any) => {
          audioChunks.push(event.data);
        });

        const start = () => mediaRecorder.start();

        const stop = () => new Promise((resolveStop, rejectStop) => {
          mediaRecorder.addEventListener('stop', async () => {
            stream.getTracks().forEach((track) => track.stop());
            const audioContext = new AudioContext();
            try {
              // Decode the raw recorded blob via Web Audio API, then re-encode as 16-bit PCM WAV.
              // WAV is lossless, browser-playable, and the safest format for Twilio's <Play> verb and PSTN telephony.
              const rawBlob = new Blob(audioChunks, { type: mimeType });
              const arrayBuffer = await rawBlob.arrayBuffer();
              const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

              const audioBlob = this.audioBufferToWavBlob(audioBuffer);
              const audioUrl = URL.createObjectURL(audioBlob);
              const filename = this.outFilename.replace(/\.[^.]+$/, '.wav');

              this.$emit('on-audio-recorded', {
                blob: audioBlob,
                url: audioUrl,
                file: this.blobToFile(audioBlob, filename),
              });
              resolveStop({ audioBlob, audioUrl });
            } catch (err) {
              rejectStop(err);
            } finally {
              audioContext.close();
            }
          });

          mediaRecorder.stop();
        });

        resolve({ start, stop });
      });
    },

    /**
     * Encodes an AudioBuffer as 16-bit PCM WAV. Must stay WAV since Twilio's <Play> verb
     * produces loud noise or will break with the browser-native WebM/Opus or MP4/AAC formats.
     */
    audioBufferToWavBlob(buffer: AudioBuffer): Blob {
      const numChannels = 1;
      const { sampleRate, length: numSamples } = buffer;
      const dataSize = numSamples * numChannels * 2;
      const wavBuffer = new ArrayBuffer(44 + dataSize);
      const view = new DataView(wavBuffer);

      const writeStr = (offset: number, str: string) => {
        for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
      };

      // Standard 44-byte WAV header: RIFF container → WAVE format → PCM fmt chunk (mono, 16-bit) → data chunk.
      writeStr(0, 'RIFF');
      view.setUint32(4, 36 + dataSize, true);
      writeStr(8, 'WAVE');
      writeStr(12, 'fmt ');
      view.setUint32(16, 16, true); // PCM chunk size
      view.setUint16(20, 1, true); // PCM format
      view.setUint16(22, numChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * numChannels * 2, true); // byte rate
      view.setUint16(32, numChannels * 2, true); // block align
      view.setUint16(34, 16, true); // bits per sample
      writeStr(36, 'data');
      view.setUint32(40, dataSize, true);

      const channelData = buffer.getChannelData(0);
      let offset = 44;
      for (let i = 0; i < numSamples; i += 1) {
        const s = Math.max(-1, Math.min(1, channelData[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        offset += 2;
      }

      return new Blob([wavBuffer], { type: 'audio/wav' });
    },

    blobToFile(blob: Blob, fileName: string): File {
      return new File([blob], fileName, {
        lastModified: new Date().getTime(),
        type: blob.type,
      });
    },
  },
});
</script>

<style lang="scss">

@keyframes blink {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.1;
  }
}
@-webkit-keyframes blink {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.1;
  }
}
.blink {
  animation: blink 1s infinite;
  -webkit-animation: blink 1s infinite;
}

</style>
